import { apiRequest, isAmbiguousTransportError } from "./client";

function queryString(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function base(projectId) {
  return `/projects/${projectId}/erp`;
}

function createMovementDraft(projectId, payload) {
  return apiRequest(`${base(projectId)}/movements/draft`, {
    method: "POST",
    body: JSON.stringify(payload),
    timeoutProfile: "mutation",
    // [WP-STABLE][MOVEMENT-TRANSPORT-RECOVERY]
    // STATUS: CONTRACT_SAFE / STATIC_PASS / STAGING_PENDING.
    // El backend reutiliza idempotencyKey, por lo que dos reintentos de transporte
    // recuperan cortes breves sin crear dos borradores del mismo movimiento.
    retryMode: payload?.idempotencyKey ? "idempotent" : undefined,
    retryAttempts: payload?.idempotencyKey ? 2 : 0,
  });
}

function postMovementAction(projectId, movementId, action, payload = {}) {
  return apiRequest(`${base(projectId)}/movements/${movementId}/${action}`, {
    method: "POST",
    body: JSON.stringify(payload),
    timeoutProfile: "mutation",
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function recoverConfirmedMovement(projectId, movementId) {
  const delays = [700, 1400, 2600];
  for (const delay of delays) {
    await sleep(delay);
    try {
      const current = await apiRequest(`${base(projectId)}/movements/${movementId}`, {
        retryAttempts: 1,
      });
      if (current?.movement?.status === "confirmed") {
        return { ...current, recoveredAfterTransportError: true };
      }
      if (current?.movement?.status && current.movement.status !== "draft") return null;
    } catch {
      // Una consulta de recuperación también puede coincidir con una caída breve.
      // Se agotan los intentos antes de devolver el error original.
    }
  }
  return null;
}

async function confirmMovement(projectId, movementId) {
  try {
    return await postMovementAction(projectId, movementId, "confirm");
  } catch (error) {
    const alreadyProcessed = error?.status === 409 && /ya fue procesado/i.test(String(error?.message || ""));
    if (!alreadyProcessed && !isAmbiguousTransportError(error)) throw error;

    const recovered = await recoverConfirmedMovement(projectId, movementId);
    if (recovered) return recovered;
    throw error;
  }
}

function voidMovement(projectId, movementId, reason) {
  return postMovementAction(projectId, movementId, "void", { reason });
}

function removeMovementAsOwner(projectId, movementId, reason) {
  return apiRequest(`${base(projectId)}/movements/${movementId}`, {
    method: "DELETE",
    body: JSON.stringify({ reason }),
    timeoutProfile: "mutation",
  });
}

// [WP-STABLE][OWNER-HARD-DELETE]
// Usa el DELETE owner que ya existe en producción. La confirmación literal en
// el body permite al backend distinguir la purga irreversible de la corrección
// auditada legacy sin depender de registrar una ruta HTTP nueva.
function hardDeleteMovementAsOwner(projectId, movementId, reason) {
  return apiRequest(`${base(projectId)}/movements/${movementId}`, {
    method: "DELETE",
    body: JSON.stringify({ reason, confirmation: "ELIMINAR DEFINITIVAMENTE" }),
    timeoutProfile: "mutation",
  });
}

export const erpApi = {
  bootstrap(projectId) {
    return apiRequest(`${base(projectId)}/bootstrap`);
  },

  overview(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/stats/overview${queryString(params)}`);
  },

  businessHealth(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/stats/business-health${queryString(params)}`);
  },

  futureEconomy(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/future-economy${queryString(params)}`);
  },

  updateFutureEconomySettings(projectId, payload) {
    return apiRequest(`${base(projectId)}/future-economy/settings`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  createFixedExpense(projectId, payload) {
    return apiRequest(`${base(projectId)}/fixed-expenses`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateFixedExpense(projectId, expenseId, payload) {
    return apiRequest(`${base(projectId)}/fixed-expenses/${expenseId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  deleteFixedExpense(projectId, expenseId) {
    return apiRequest(`${base(projectId)}/fixed-expenses/${expenseId}`, { method: "DELETE" });
  },

  reconciliation(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/reconciliation${queryString(params)}`);
  },

  getAccountingPeriod(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/accounting-period${queryString(params)}`);
  },

  closeAccountingPeriod(projectId, payload) {
    return apiRequest(`${base(projectId)}/accounting-period/close`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  reopenAccountingPeriod(projectId, payload) {
    return apiRequest(`${base(projectId)}/accounting-period/reopen`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  listAccounts(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/accounts${queryString(params)}`);
  },

  createAccount(projectId, payload) {
    return apiRequest(`${base(projectId)}/accounts`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateAccount(projectId, accountId, payload) {
    return apiRequest(`${base(projectId)}/accounts/${accountId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  listTaxProfiles(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/tax-profiles${queryString(params)}`);
  },

  createTaxProfile(projectId, payload) {
    return apiRequest(`${base(projectId)}/tax-profiles`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateTaxProfile(projectId, taxProfileId, payload) {
    return apiRequest(`${base(projectId)}/tax-profiles/${taxProfileId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  listMovements(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/movements${queryString(params)}`);
  },

  getMovement(projectId, movementId) {
    return apiRequest(`${base(projectId)}/movements/${movementId}`);
  },

  markMovementsHistorical(projectId, movementIds, reason = "") {
    return apiRequest(`${base(projectId)}/movements/mark-historical`, {
      method: "POST",
      body: JSON.stringify({ movementIds, reason }),
      timeoutProfile: "mutation",
    });
  },

  createMovementDraft,

  // Alias compatible: los consumidores actuales siguen creando un borrador.
  createMovement(projectId, payload) {
    return createMovementDraft(projectId, payload);
  },

  createConfirmedMovement(projectId, payload) {
    return apiRequest(`${base(projectId)}/movements/confirm-now`, {
      method: "POST",
      body: JSON.stringify(payload),
      timeoutProfile: "mutation",
      retryMode: payload?.idempotencyKey ? "idempotent" : undefined,
    });
  },

  updateMovement(projectId, movementId, payload) {
    return apiRequest(`${base(projectId)}/movements/${movementId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      timeoutProfile: "mutation",
    });
  },

  confirmMovement,

  receivePurchaseItem(projectId, movementId, itemId, payload = {}) {
    return apiRequest(`${base(projectId)}/movements/${movementId}/items/${itemId}/receive`, {
      method: "POST",
      body: JSON.stringify(payload),
      timeoutProfile: "mutation",
    });
  },

  voidMovement,
  removeMovementAsOwner,
  hardDeleteMovementAsOwner,

  // Nombre explícito para el ciclo nuevo; conserva voidMovement para compatibilidad.
  annulMovement(projectId, movementId, reason) {
    return voidMovement(projectId, movementId, reason);
  },

  listLedger(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/ledger${queryString(params)}`);
  },

  ledgerBalances(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/ledger/balances${queryString(params)}`);
  },

  listTransactions(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/transactions${queryString(params)}`);
  },

  listPendingStockReceipts(projectId) {
    return apiRequest(`${base(projectId)}/stock-pending-receipts`);
  },

  listStockMovements(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/stock-movements${queryString(params)}`);
  },

  getActiveStockCount(projectId) {
    return apiRequest(`${base(projectId)}/stock-counts/active`);
  },

  startStockCount(projectId) {
    return apiRequest(`${base(projectId)}/stock-counts`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  getStockCount(projectId, countId, params = {}) {
    return apiRequest(`${base(projectId)}/stock-counts/${countId}${queryString(params)}`);
  },

  updateStockCountLine(projectId, countId, lineId, countedQuantity) {
    return apiRequest(`${base(projectId)}/stock-counts/${countId}/lines/${lineId}`, {
      method: "PATCH",
      body: JSON.stringify({ countedQuantity }),
    });
  },

  refreshStockCountCatalog(projectId, countId) {
    return apiRequest(`${base(projectId)}/stock-counts/${countId}/refresh-catalog`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  reviewStockCount(projectId, countId, params = {}) {
    return apiRequest(`${base(projectId)}/stock-counts/${countId}/review${queryString(params)}`);
  },

  confirmStockCount(projectId, countId, { allowPending = false } = {}) {
    return apiRequest(`${base(projectId)}/stock-counts/${countId}/confirm`, {
      method: "POST",
      body: JSON.stringify({ allowPending }),
    });
  },

  discardStockCount(projectId, countId) {
    return apiRequest(`${base(projectId)}/stock-counts/${countId}/discard`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  getMercadoPagoSettlement(projectId, paymentId) {
    return apiRequest(`${base(projectId)}/cash/mercadopago/payments/${encodeURIComponent(paymentId)}/settlement`, {
      retryAttempts: 1,
    });
  },

  listCashRegisters(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/cash/registers${queryString(params)}`);
  },

  createCashRegister(projectId, payload) {
    return apiRequest(`${base(projectId)}/cash/registers`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateCashRegister(projectId, registerId, payload) {
    return apiRequest(`${base(projectId)}/cash/registers/${registerId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  deleteCashRegister(projectId, registerId) {
    return apiRequest(`${base(projectId)}/cash/registers/${registerId}`, {
      method: "DELETE",
    });
  },

  openCashRegister(projectId, registerId, payload) {
    return apiRequest(`${base(projectId)}/cash/registers/${registerId}/open`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  listCashSessions(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/cash/sessions${queryString(params)}`);
  },

  closeCashSession(projectId, cashSessionId, payload) {
    return apiRequest(`${base(projectId)}/cash/sessions/${cashSessionId}/close`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  reviewCashSession(projectId, cashSessionId, payload) {
    return apiRequest(`${base(projectId)}/cash/sessions/${cashSessionId}/review`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  listCashMovements(projectId, params = {}) {
    return apiRequest(`${base(projectId)}/cash/movements${queryString(params)}`);
  },

  recordCashOperation(projectId, payload) {
    return apiRequest(`${base(projectId)}/cash/operations`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};
